import { createSlice } from '@reduxjs/toolkit';
import type { ResolvedDisplayColumn } from '../../services/api/lineDisplayProfileService';
import { resolveLineDisplayProfile, saveLineDisplayProfile } from '../thunks/lineDisplayProfileThunks';

// ============================================================================
// Buyer line display profile slice — Spec buyer-line-display-profile (group 8).
//
// Holds the resolved effective columns (registry baseline + overrides, deepest
// wins) with per-column provenance for the currently selected scope, the BE
// scope path, and load/save status + the BE validation messages from the last
// save attempt. The editor's draft lives locally in the component.
// ============================================================================

export interface LineDisplayProfileState {
  columns: ResolvedDisplayColumn[];
  scopePath: string;
  gridKind: string;
  resolveStatus: 'idle' | 'loading' | 'success' | 'failure';
  saveStatus: 'idle' | 'saving' | 'success' | 'failure';
  loadError: string | null;
  saveErrors: string[];
}

const initialState: LineDisplayProfileState = {
  columns: [],
  scopePath: '',
  gridKind: '',
  resolveStatus: 'idle',
  saveStatus: 'idle',
  loadError: null,
  saveErrors: [],
};

const lineDisplayProfileSlice = createSlice({
  name: 'lineDisplayProfile',
  initialState,
  reducers: {
    clearLineDisplaySaveErrors: (state) => { state.saveErrors = []; },
    resetLineDisplayProfile: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(resolveLineDisplayProfile.pending, (state) => {
        state.resolveStatus = 'loading';
        state.loadError = null;
      })
      .addCase(resolveLineDisplayProfile.fulfilled, (state, action) => {
        state.resolveStatus = 'success';
        state.columns = action.payload.columns;
        state.scopePath = action.payload.scopePath;
        state.gridKind = action.payload.gridKind;
      })
      .addCase(resolveLineDisplayProfile.rejected, (state, action) => {
        state.resolveStatus = 'failure';
        state.loadError = (action.payload as string) ?? action.error.message ?? 'Failed to resolve line display profile';
      });

    builder
      .addCase(saveLineDisplayProfile.pending, (state) => {
        state.saveStatus = 'saving';
        state.saveErrors = [];
      })
      .addCase(saveLineDisplayProfile.fulfilled, (state, action) => {
        state.saveStatus = 'success';
        state.scopePath = action.payload.scopePath;
      })
      .addCase(saveLineDisplayProfile.rejected, (state, action) => {
        state.saveStatus = 'failure';
        state.saveErrors = (action.payload as string[]) ?? [action.error.message ?? 'Failed to save line display profile'];
      });
  },
});

export const { clearLineDisplaySaveErrors, resetLineDisplayProfile } = lineDisplayProfileSlice.actions;
export default lineDisplayProfileSlice.reducer;
